import { Fragment, forwardRef, isValidElement, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * The row-action and toolbar menu, described as data rather than markup so a
 * table column can build one per row without twenty lines of JSX.
 */

export interface MenuItemSpec {
  key: string;
  /** Arabic. */
  label: string;
  icon?: LucideIcon;
  onSelect?: () => void;
  /** red-600 — destructive commands read as destructive before they are clicked. */
  danger?: boolean;
  disabled?: boolean;
  /** DS-03 — the shortcut is shown next to the command, never left to be discovered. */
  shortcut?: string;
  separatorBefore?: boolean;
  /** Nested submenu; `onSelect` and `shortcut` are ignored when present. */
  items?: MenuItemSpec[];
}

type ContentProps = ComponentPropsWithoutRef<typeof Menu.Content>;

export interface DropdownMenuProps extends Omit<ContentProps, 'children'> {
  /** A single element that can take a ref; anything else is wrapped in a span. */
  trigger: ReactNode;
  items: MenuItemSpec[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Radix's modal mode traps focus and locks the page behind the menu. */
  modal?: boolean;
  emptyLabel?: string;
}

const SURFACE_CLASS =
  'z-50 min-w-[11rem] max-h-[70vh] overflow-y-auto animate-zoomIn rounded border border-slate-300 bg-white p-1 shadow-lg';

function itemClass(item: MenuItemSpec): string {
  return cn(
    'flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-none transition',
    item.danger
      ? 'text-red-600 data-[highlighted]:bg-red-50'
      : 'text-slate-700 data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900',
    'data-[state=open]:bg-slate-100',
    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40',
  );
}

function renderItems(items: MenuItemSpec[]): ReactNode {
  return items.map((item) => {
    const Icon = item.icon;
    const body = (
      <>
        {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden /> : null}
        <span className="flex-1 truncate text-start">{item.label}</span>
      </>
    );

    return (
      <Fragment key={item.key}>
        {item.separatorBefore ? <Menu.Separator className="my-1 h-px bg-slate-200" /> : null}

        {item.items?.length ? (
          <Menu.Sub>
            <Menu.SubTrigger data-testid={`menu-item-${item.key}`} disabled={item.disabled} className={itemClass(item)}>
              {body}
              {/* The submenu opens toward the inline end, so the chevron follows it. */}
              <ChevronRight className="flip-rtl h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
            </Menu.SubTrigger>
            <Menu.Portal>
              <Menu.SubContent className={SURFACE_CLASS} sideOffset={2} alignOffset={-4}>
                {renderItems(item.items)}
              </Menu.SubContent>
            </Menu.Portal>
          </Menu.Sub>
        ) : (
          <Menu.Item
            data-testid={`menu-item-${item.key}`}
            disabled={item.disabled}
            className={itemClass(item)}
            onSelect={
              item.onSelect
                ? () => {
                    item.onSelect?.();
                  }
                : undefined
            }
          >
            {body}
            {item.shortcut ? (
              <span className="ltr-num shrink-0 ps-3 font-mono text-[10px] tracking-wide text-slate-400">
                {item.shortcut}
              </span>
            ) : null}
          </Menu.Item>
        )}
      </Fragment>
    );
  });
}

export const DropdownMenu = forwardRef<HTMLDivElement, DropdownMenuProps>(function DropdownMenu(
  {
    trigger,
    items,
    align = 'end',
    sideOffset = 4,
    open,
    onOpenChange,
    modal = false,
    emptyLabel: emptyLabelProp,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const emptyLabel = emptyLabelProp ?? t('ui.menuEmpty');
  const triggerNode = isValidElement(trigger) ? trigger : <span className="inline-flex">{trigger}</span>;

  // DS-50 — Radix needs the direction to flip `align` and to open submenus on the
  // correct side; without it every menu behaves as if the page were English.
  const dir = typeof document !== 'undefined' && document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';

  return (
    <Menu.Root dir={dir} modal={modal} open={open} onOpenChange={onOpenChange}>
      <Menu.Trigger asChild>{triggerNode}</Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          className={cn(SURFACE_CLASS, className)}
          data-testid="dropdown-menu"
          {...rest}
        >
          {items.length ? (
            renderItems(items)
          ) : (
            <div className="px-2 py-1.5 text-xs text-slate-400">{emptyLabel}</div>
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
});
